import { PaletteMode, ThemeOptions } from '@mui/material'
import { isLowSpec } from './deviceSpecCheck'

const isLowSpecDevice = isLowSpec()
const lightPalette = {
    owDefault: {
        main: '#EAEAEA',
        dark: '#E0E0E0',
        light: '#EAEAEA',
    },
    bluegrey: {
        main: '#A5B1C2',
        dark: '#8392a7',
        light: '#A5B1C2',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    warning: {
        main: '#ffa500',
        dark: '#ff9600',
        light: '#ffa500',
        lighter: '#ffedcc',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    info: {
        main: '#0288d1',
        dark: '#01579b',
        light: '#7cc5ec',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    error: {
        main: '#d32f2f',
        dark: '#c62828',
        light: '#f57d7d',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    primary: {
        main: '#10ac84',
        dark: '#009d72',
        light: '#56CBAC',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    invertPrimary: {
        main: '#ffffff',
        dark: '#f2f6f8',
        light: '#ffffff',
        contrastText: '#10ac84',
    },
    secondary: {
        light: '#aaa',
        main: '#676767',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    background: {
        paper: '#ffffff',
        default: '#f2f6f8',
    },
    text: {
        primary: 'rgba(0, 0, 0, 0.87)',
        light: '#676767',
    },
    numberPositive: {
        main: '#10ac84',
        dark: '#009d72',
        light: '#56CBAC',
    },
    numberNegative: {
        main: '#d32f2f',
        dark: '#c62828',
        light: '#f57d7d',
    },
    topside: {
        main: '#10ac84', // top
        contrastText: '#ffffff', // side
    },
    disabled: {
        main: 'rgba(140, 140, 140, 0.12)',
        light: 'rgba(0,0,0,0.09)',
        dark: 'rgba(0,0,0,0.12)',
        contrastText: 'rgba(0,0,0,0.26)',
    },
    outlinePrimary: {
        main: '#ffffff',
        dark: '#10ac84',
        light: '#ffffff',
        contrastText: '#10ac84',
    },
    table: {
        main: '#ffffff',
        dark: '#f2f6f8',
        light: '#f7fafc',
        contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    listCollapse: {
        main: '#efefef', // item
        light: '#ffffff', // sub list item
    },
}
const darkPalette = {
    owDefault: {
        main: '#666666',
        dark: '#363636',
        light: '#666666',
    },
    bluegrey: {
        main: '#636363',
        light: '#676972',
        dark: '#505050',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    warning: {
        main: '#ffb640',
        dark: '#ffa500',
        light: '#ffb640',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    info: {
        main: '#03a9f4',
        dark: '#0288d1',
        light: '#03a9f4',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    error: {
        main: '#ef5350',
        dark: '#d32f2f',
        light: '#ef5350',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    primary: {
        main: '#47bb99',
        dark: '#10ac84',
        light: '#47bb99',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    invertPrimary: {
        main: '#484848',
        dark: '#262626',
        light: '#383838',
        contrastText: '#47bb99',
    },
    secondary: {
        main: '#636363',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    background: {
        paper: '#323232',
        default: '#262626',
    },
    text: {
        primary: 'rgba(255, 255, 255, 0.87)',
        light: '#676767',
    },
    numberPositive: {
        main: '#47bb99',
        dark: '#10ac84',
        light: '#47bb99',
    },
    numberNegative: {
        main: '#ef5350',
        dark: '#d32f2f',
        light: '#ef5350',
    },
    topside: {
        main: '#262626', // top
        contrastText: '#323232', // side
    },
    disabled: {
        main: 'rgba(255,255,255,0.12)',
        light: 'rgba(255,255,255,0.12)',
        dark: 'rgba(255,255,255,0.12)',
        contrastText: 'rgba(255,255,255,0.3)',
    },
    outlinePrimary: {
        main: '#47bb99',
        dark: '#10ac84',
        light: '#47bb99',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    table: {
        main: '#323232',
        dark: '#262626',
        light: '#383838',
        contrastText: 'rgba(255, 255, 255, 0.87)',
    },
    listCollapse: {
        main: '#323232', // item
        light: '#262626', // sub list item
    },
}
export const newThemeV5 = (mode: PaletteMode = 'light'): ThemeOptions => {
    const palette = mode === 'light' ? lightPalette : darkPalette
    return {
        components: {
            MuiAutocomplete: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: 0,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiCardContent: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.paper,
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: { textTransform: 'uppercase' },
                },
                defaultProps: {
                    variant: 'contained',
                    color: 'secondary',
                    disableElevation: isLowSpecDevice,
                    disableRipple: isLowSpecDevice,
                    disableTouchRipple: isLowSpecDevice,
                },
            },
            MuiIconButton: {
                defaultProps: {
                    disableRipple: isLowSpecDevice,
                    disableTouchRipple: isLowSpecDevice,
                    // color: 'secondary',
                },
            },
            MuiTextField: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.topside.main,
                        color: 'rgba(255, 255, 255, 0.87)',
                    },
                },
            },
            MuiDialogContent: {
                defaultProps: {
                    dividers: true,
                },
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.paper,
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.paper,
                        width: '100%',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.default,
                        '&.Mui-selected': {
                            backgroundColor:
                                mode === 'dark'
                                    ? palette.owDefault.dark
                                    : palette.owDefault.main,
                            borderLeft: `5px solid ${palette.primary.main}`,
                        },
                    },
                },
            },
            MuiList: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.paper,
                        WebkitOverflowScrolling: 'touch',
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        marginRight: 8,
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: 0,
                        fontSize: 12,
                        padding: 10,
                    },
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        borderBottomColor: palette.table.dark,
                        backgroundColor: palette.table.main,
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.table.dark,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: palette.owDefault.main,
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.background.default,
                    },
                },
            },
            MuiRadio: {
                defaultProps: {
                    disableRipple: isLowSpecDevice,
                },
            },
            MuiCheckbox: {
                defaultProps: {
                    disableRipple: isLowSpecDevice,
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: { cursor: 'pointer' },
                },
            },
        },
        typography: {
            fontFamily: '"Open Sans", sans-serif',
        },
        palette: { mode: mode, ...palette },
        // colorSchemes: {
        //     dark: { palette: darkPalette },
        //     light: { palette: lightPalette },
        // },
    }
}
